import { supabase } from '../lib/supabase';
import { useAuth0 } from '@auth0/auth0-react';

export interface AuditLogEntry {
  id: number;
  created_at: string;
  user_id: string;
  user_email: string;
  user_name: string;
  action_type: string;
  category: string;
  resource_type?: string;
  resource_id?: string;
  details?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  error_message?: string;
}

export type ActionType = 
  | 'login'
  | 'logout'
  | 'view_unlock_code'
  | 'copy_unlock_code'
  | 'regenerate_unlock_code'
  | 'create_device'
  | 'edit_device'
  | 'delete_device'
  | 'lock_device'
  | 'unlock_device'
  | 'bulk_import_devices'
  | 'bulk_lock_devices'
  | 'bulk_unlock_devices'
  | 'bulk_delete_devices'
  | 'admin_registration'
  | 'admin_approval'
  | 'admin_denial'
  | 'view_admin_profile'
  | 'edit_admin_profile'
  | 'view_audit_logs'
  | 'system_status_check'
  | 'finance_registration'
  | 'finance_approval'
  | 'finance_denial'
  | 'create_lock_request'
  | 'approve_lock_request'
  | 'deny_lock_request'
  | 'run_db_backup'
  | 'fetch_table_counts'
  | 'role_change'
  | 'create_lock_message'
  | 'update_lock_message'
  | 'delete_lock_message'
  | 'set_default_lock_message';

export type Category = 
  | 'authentication'
  | 'device_management'
  | 'admin_management'
  | 'system_monitoring'
  | 'security';

export function useAuditLog() {
  const { user } = useAuth0();

  const logAction = async (
    actionType: ActionType,
    category: Category,
    options: {
      resourceType?: string;
      resourceId?: string;
      details?: Record<string, any>;
      success?: boolean;
      errorMessage?: string;
    } = {}
  ) => {
    if (!user) return;

    try {
      // Get user's IP address and user agent
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      const auditEntry = {
        user_id: user.sub,
        user_email: user.email,
        user_name: user.name || `${user.given_name || ''} ${user.family_name || ''}`.trim(),
        action_type: actionType,
        category: category,
        resource_type: options.resourceType,
        resource_id: options.resourceId,
        details: options.details,
        ip_address: ipData.ip,
        user_agent: navigator.userAgent,
        success: options.success ?? true,
        error_message: options.errorMessage
      };

      const { error } = await supabase
        .from('audit_log')
        .insert(auditEntry);

      if (error) {
        console.error('Failed to log audit entry:', error);
      }
    } catch (error) {
      console.error('Error logging audit entry:', error);
    }
  };

  const getAuditLogs = async (filters: {
    category?: Category;
    actionType?: ActionType;
    userId?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
    offset?: number;
  } = {}) => {
    let query = supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    if (filters.actionType) {
      query = query.eq('action_type', filters.actionType);
    }

    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }

    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate.toISOString());
    }

    if (filters.endDate) {
      query = query.lte('created_at', filters.endDate.toISOString());
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching audit logs:', error);
      return { logs: [], error };
    }

    return { logs: data as AuditLogEntry[], error: null };
  };

  return {
    logAction,
    getAuditLogs
  };
}
